import React, { useLayoutEffect, useRef, useState } from 'react';
import { MessageSquare, AlertTriangle } from 'lucide-react';
import AgentMarkdownContent from 'features/agent-analytics/components/AgentMarkdownContent';
import DetailHoverCard from 'shared/components/ui/DetailHoverCard';
import {
  RichValueView, StatusDot, ScorePill, formatDuration, formatCost,
} from 'features/agent-analytics/components/runCells';
import { extractUserTurn, extractAssistantTurn } from 'features/agent-analytics/utils/conversationTurns';

/**
 * A conversation rendered as a chat transcript.
 *
 * Each run contributes one exchange: the user turn from the run's input, the
 * agent turn from its output. This replaced a numbered `#1 #2 #3` picker that
 * told you how many runs there were but nothing about what was said.
 *
 * Clicking an exchange opens that run — in the conversation drawer that stacks
 * a run drawer on top; in the run drawer's Conversations tab it swaps runs.
 */

/** Clock time only. The date lives on the conversation, not on every turn. */
function clockTime(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
}

const COLLAPSED_MAX_PX = 220;

/**
 * A message body that collapses past a few lines behind "View more".
 *
 * Measured rather than character-counted: the bodies here are markdown with
 * tables and code blocks, where character count is a poor proxy for height —
 * a short string of JSON can render taller than a long paragraph.
 */
function CollapsibleBody({ children }) {
  const ref = useRef(null);
  const [overflows, setOverflows] = useState(false);
  const [open, setOpen] = useState(false);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const measure = () => setOverflows(el.scrollHeight > COLLAPSED_MAX_PX + 8);
    measure();
    // Markdown can reflow after mount (fonts, tables), so re-measure on resize.
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    if (ro) ro.observe(el);
    return () => ro?.disconnect();
  }, [children]);

  return (
    <>
      <div
        ref={ref}
        className="relative overflow-hidden"
        style={open ? undefined : { maxHeight: COLLAPSED_MAX_PX }}
      >
        {children}
        {overflows && !open ? (
          // Fade the clipped edge so it reads as "there is more" rather than a
          // sentence that happens to end mid-air.
          <div
            className="pointer-events-none absolute inset-x-0 bottom-0 h-10"
            style={{ background: 'linear-gradient(to bottom, transparent, var(--fade-to, transparent))' }}
          />
        ) : null}
      </div>
      {overflows ? (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
          className="mt-1 w-full text-center text-[11px] font-medium text-[color:var(--text-muted)] transition-colors hover:text-[color:var(--text-primary)]"
        >
          {open ? 'View less' : 'View more'}
        </button>
      ) : null}
    </>
  );
}

function Bubble({ side, turn }) {
  const isUser = side === 'user';
  return (
    <div className={`flex ${isUser ? 'justify-start' : 'justify-end'}`}>
      <div
        className={`min-w-0 max-w-[86%] rounded-xl px-3 py-2 text-[12px] leading-relaxed ${
          isUser
            ? 'border border-[color:var(--border-default)] bg-surface text-[color:var(--text-primary)]'
            : 'bg-[color:var(--surface-sunken)] text-[color:var(--text-primary)]'
        }`}
        // Drives CollapsibleBody's fade so it matches whichever bubble it's in.
        style={{ '--fade-to': isUser ? 'var(--surface-base)' : 'var(--surface-sunken)' }}
      >
        <CollapsibleBody>
          {turn.text ? <AgentMarkdownContent>{turn.text}</AgentMarkdownContent> : <RichValueView value={turn.value} />}
        </CollapsibleBody>
      </div>
    </div>
  );
}

function EmptyBubble({ side, label }) {
  const isUser = side === 'user';
  return (
    <div className={`flex ${isUser ? 'justify-start' : 'justify-end'}`}>
      <span className="rounded-xl border border-dashed border-[color:var(--border-default)] px-3 py-1.5 text-[11px] text-[color:var(--text-muted)]">
        {label}
      </span>
    </div>
  );
}

/**
 * The run's own line, sitting above its answer: just when it replied.
 * Agent, status, duration, cost, rage and satisfaction are one hover away. The
 * agent is already named in the drawer's header, and a green dot on every
 * healthy reply said nothing — a run that went wrong is marked on the whole
 * exchange instead (see `needsAttention`).
 */
function RunMetaLine({ run }) {
  // When the answer landed, not when the question was asked — the question's
  // time already sits above the user bubble, and repeating it there made both
  // timestamps look like a rendering bug. Derived from started_at + duration
  // rather than ended_at, which is unreliable on runs that never closed.
  const repliedAt = run.started_at && run.duration_ms != null
    ? new Date(new Date(run.started_at).getTime() + Number(run.duration_ms)).toISOString()
    : run.started_at;
  const time = clockTime(repliedAt);
  const rows = [
    run.status && { label: 'Status', value: <span className="inline-flex items-center gap-1.5"><StatusDot status={run.status} />{run.status}</span> },
    run.duration_ms != null && { label: 'Duration', value: formatDuration(run.duration_ms) },
    run.total_cost ? { label: 'Cost', value: formatCost(run.total_cost) } : null,
    run.rage_score != null && { label: 'Rage', value: <ScorePill value={run.rage_score} kind="rage" /> },
    run.satisfaction_score != null && { label: 'Satisfaction', value: <ScorePill value={run.satisfaction_score} kind="sat" /> },
  ];
  if (!time) return null;
  return (
    <div className="flex justify-end">
      <DetailHoverCard title={run.agent_name || 'Run'} rows={rows} className="text-[11px] tabular-nums text-[color:var(--text-muted)]">
        {time}
      </DetailHoverCard>
    </div>
  );
}

// Anything but a clean or still-running run is worth a second look.
const QUIET_STATUSES = new Set(['ok', 'success', 'running', 'in_progress', '']);
function needsAttention(run) {
  return !QUIET_STATUSES.has(String(run.status || '').toLowerCase());
}

function Exchange({ run, isActive, onOpenRun }) {
  const user = extractUserTurn(run.input);
  const agent = extractAssistantTurn(run.output);
  const isError = run.status === 'error';
  const flagged = needsAttention(run);
  const askedAt = clockTime(run.started_at);

  return (
    <li>
      <div
        role={onOpenRun ? 'button' : undefined}
        tabIndex={onOpenRun ? 0 : undefined}
        onClick={onOpenRun ? () => onOpenRun(run.run_id) : undefined}
        onKeyDown={
          onOpenRun
            ? (e) => { if (e.key === 'Enter') { e.preventDefault(); onOpenRun(run.run_id); } }
            : undefined
        }
        // A run that went wrong tints its whole exchange, question and reply,
        // so it stands out of a long transcript. The open run is marked the
        // way the trace tree marks its selected row — a neutral fill — not with
        // an orange border, which read as an alert rather than "you are here".
        className={`flex flex-col gap-1.5 rounded-lg border p-3 transition-colors ${
          flagged
            ? `bg-[color:var(--feedback-warning-bg)] ${isActive ? 'border-[color:var(--feedback-warning)]' : 'border-transparent'}`
            : isActive
              ? 'border-[color:var(--border-default)] bg-[color:var(--surface-hover)]'
              : 'border-transparent hover:bg-[color:var(--surface-sunken)]'
        } ${onOpenRun ? 'cursor-pointer' : ''}`}
      >
        {askedAt ? (
          <span className="text-[11px] tabular-nums text-[color:var(--text-muted)]">{askedAt}</span>
        ) : null}

        {user ? <Bubble side="user" turn={user} /> : <EmptyBubble side="user" label="no input recorded" />}

        {user?.truncatedHistory > 0 ? (
          <span className="text-[10px] text-[color:var(--text-muted)]">
            + {user.truncatedHistory} earlier message{user.truncatedHistory === 1 ? '' : 's'} in this prompt
          </span>
        ) : null}

        <RunMetaLine run={run} />

        {isError ? (
          <div className="flex justify-end">
            <span className="inline-flex max-w-[86%] items-start gap-1.5 rounded-xl border border-[color:var(--feedback-error-bg)] bg-[color:var(--feedback-error-bg)] px-3 py-2 text-[12px] text-[color:var(--feedback-error)]">
              <AlertTriangle size={13} className="mt-px flex-none" aria-hidden />
              <span className="min-w-0 break-words">{run.error_summary || run.error_type || 'This run ended in an error.'}</span>
            </span>
          </div>
        ) : agent ? (
          <Bubble side="agent" turn={agent} />
        ) : (
          <EmptyBubble side="agent" label="no output recorded" />
        )}
      </div>
    </li>
  );
}

export default function ConversationChat({
  runs = [],
  activeRunId = null,
  onOpenRun,
  loading = false,
  error = null,
  emptyMessage = 'No runs in this conversation.',
}) {
  if (loading) {
    return <div className="py-10 text-center text-[12px] text-[color:var(--text-muted)]">Loading conversation…</div>;
  }
  if (error) {
    return (
      <div className="m-3 rounded-md border border-[color:var(--feedback-error-bg)] bg-[color:var(--feedback-error-bg)] p-3 text-[12px] text-[color:var(--feedback-error)]">
        {error}
      </div>
    );
  }
  if (!runs.length) {
    return (
      <div className="flex flex-col items-center gap-2 py-10 text-[12px] text-[color:var(--text-muted)]">
        <MessageSquare size={18} aria-hidden />
        {emptyMessage}
      </div>
    );
  }

  return (
    <ol className="flex flex-col gap-1 p-3">
      {runs.map((run) => (
        <Exchange key={run.run_id} run={run} isActive={activeRunId === run.run_id} onOpenRun={onOpenRun} />
      ))}
    </ol>
  );
}
